/**
 * useCanvasGuidedMode — Step-by-step guided canvas filling.
 *
 * Manages step navigation, question resolution with placeholders,
 * and AI suggestion fetching with per-section caching.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { canvasAIApi } from '@/lib/api/client'
import { resolvePlaceholders, type SectionStats, type SectionItem } from '@ispot/shared'
import type { GuidedQuestion } from '@ispot/shared'

interface AISuggestion {
  id: string
  label: string
  description?: string
  entityType: string
  confidence: 'high' | 'medium' | 'low'
}

interface UseCanvasGuidedModeOptions {
  questions: GuidedQuestion[]
  sectionStats: Record<string, SectionStats>
  sectionItems: Record<string, SectionItem[]>
  canvasContext: Record<string, string[]>
  accountId?: string
  projectId?: string
  canvasInstanceUri: string | null
  apiKey: string | null
  initialSectionId?: string | null
}

// Cache key = "sectionId:itemCount" — invalidates when items are added/removed
function cacheKey(sectionId: string, stats: Record<string, SectionStats>): string {
  return `${sectionId}:${stats[sectionId]?.count ?? 0}`
}

export function useCanvasGuidedMode({
  questions,
  sectionStats,
  sectionItems,
  canvasContext,
  accountId,
  projectId,
  canvasInstanceUri,
  apiKey,
  initialSectionId,
}: UseCanvasGuidedModeOptions) {
  const sortedQuestions = useMemo(
    () => [...questions].sort((a, b) => a.order - b.order),
    [questions],
  )

  // Determine initial step from target section
  const initialStep = useMemo(() => {
    if (!initialSectionId) return 0
    const idx = sortedQuestions.findIndex((q) => q.sectionId === initialSectionId)
    return idx >= 0 ? idx : 0
  }, [initialSectionId, sortedQuestions])

  const [currentStep, setCurrentStep] = useState(initialStep)
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([])
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false)
  const [suggestionError, setSuggestionError] = useState<string | null>(null)

  // Suggestion cache: key → suggestions
  const suggestionsCache = useRef<Map<string, AISuggestion[]>>(new Map())

  // Reset step when initial section changes (e.g., from challenge navigation)
  useEffect(() => {
    if (initialSectionId) {
      const idx = sortedQuestions.findIndex((q) => q.sectionId === initialSectionId)
      if (idx >= 0) setCurrentStep(idx)
    }
  }, [initialSectionId, sortedQuestions])

  const currentQuestion = sortedQuestions[currentStep]

  // Resolve placeholders in question text
  const resolvedQuestion = useMemo(() => {
    if (!currentQuestion) return ''
    return resolvePlaceholders(currentQuestion.question, sectionStats, sectionItems)
  }, [currentQuestion, sectionStats, sectionItems])

  const resolvedHint = useMemo(() => {
    if (!currentQuestion) return ''
    return resolvePlaceholders(currentQuestion.hint, sectionStats, sectionItems)
  }, [currentQuestion, sectionStats, sectionItems])

  // Navigation
  const goToStep = useCallback((step: number) => {
    setCurrentStep(Math.max(0, Math.min(step, sortedQuestions.length - 1)))
  }, [sortedQuestions.length])

  const goNext = useCallback(() => {
    setCurrentStep((s) => Math.min(s + 1, sortedQuestions.length - 1))
  }, [sortedQuestions.length])

  const goPrev = useCallback(() => {
    setCurrentStep((s) => Math.max(s - 1, 0))
  }, [])

  const isFirst = currentStep === 0
  const isLast = currentStep === sortedQuestions.length - 1

  // Build fallback suggestions from template
  const buildFallbacks = useCallback((question: GuidedQuestion): AISuggestion[] => {
    return (question.fallbackSuggestions || []).map((label, i) => ({
      id: `fallback-${i}`,
      label,
      entityType: '',
      confidence: 'medium' as const,
    }))
  }, [])

  // Fetch AI suggestions for current step (with cache)
  const fetchSuggestions = useCallback(async (forceRefresh = false) => {
    if (!currentQuestion) return

    const key = cacheKey(currentQuestion.sectionId, sectionStats)

    // Check cache first (unless forced refresh)
    if (!forceRefresh && suggestionsCache.current.has(key)) {
      setSuggestions(suggestionsCache.current.get(key)!)
      return
    }

    if (!apiKey || !accountId || !projectId || !canvasInstanceUri) {
      const fallbacks = buildFallbacks(currentQuestion)
      setSuggestions(fallbacks)
      suggestionsCache.current.set(key, fallbacks)
      return
    }

    setIsLoadingSuggestions(true)
    setSuggestionError(null)

    try {
      const result = await canvasAIApi.generateSuggestions(
        accountId,
        projectId,
        canvasInstanceUri,
        {
          sectionKey: currentQuestion.sectionId,
          apiKey,
          canvasContext,
          limit: 6,
        },
      )
      setSuggestions(result.suggestions)
      suggestionsCache.current.set(key, result.suggestions)
    } catch {
      setSuggestionError('Could not generate suggestions')
      const fallbacks = buildFallbacks(currentQuestion)
      setSuggestions(fallbacks)
      suggestionsCache.current.set(key, fallbacks)
    } finally {
      setIsLoadingSuggestions(false)
    }
  }, [apiKey, accountId, projectId, canvasInstanceUri, currentQuestion, canvasContext, sectionStats, buildFallbacks])

  // Auto-fetch suggestions when step changes or instance changes
  useEffect(() => {
    fetchSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, canvasInstanceUri])

  // Section completion stats
  const completedSections = useMemo(
    () => sortedQuestions.filter((q) => (sectionStats[q.sectionId]?.count || 0) > 0).length,
    [sortedQuestions, sectionStats],
  )

  // Expose forceRefresh for the refresh button
  const refreshSuggestions = useCallback(() => fetchSuggestions(true), [fetchSuggestions])

  return {
    currentStep,
    totalSteps: sortedQuestions.length,
    currentQuestion,
    resolvedQuestion,
    resolvedHint,
    suggestions,
    isLoadingSuggestions,
    suggestionError,
    goToStep,
    goNext,
    goPrev,
    isFirst,
    isLast,
    completedSections,
    fetchSuggestions: refreshSuggestions,
  }
}
